import { useGrowthBook } from '@growthbook/growthbook-react';
import { Grid } from '@mui/material';
import BundlingTimer from 'components/BundlingTimer/BundlingTimer';
import { AppFeatures, FEATURES_READY_TIMEOUT_MS } from 'components/GrowthBookWrapper/GrowthBookWrapper';
import { usePriceFormatter } from 'hooks/usePriceFormatter';
import { useUntrackedFeatureIsOn } from 'hooks/useUntrackedFeatureIsOn';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hooks';
import { DeliveryOptionWithTotals } from 'types/delivery.types';
import { getCampaignDeliveryLine, orderByPrice, orderLikeCheckout } from 'utils/getProductPageDeliveryLine';

interface ShippingInformationProps {
  shippingOptionsWithTotals: Record<string, DeliveryOptionWithTotals>;
  status: string;
  handleBundlingTimeEnd: () => void;
}

export const ShippingInformation = ({
  shippingOptionsWithTotals,
  status,
  handleBundlingTimeEnd,
}: ShippingInformationProps): JSX.Element | null => {
  const { t } = useTranslation();
  const formatPrice = usePriceFormatter();
  const gb = useGrowthBook<AppFeatures>();
  const user = useAppSelector(({ userData }) => userData?.user);
  const isUserLoading = useAppSelector(({ userData }) => userData?.isLoading);
  const isRankingFlagOn = useUntrackedFeatureIsOn('checkout_delivery_options_rank_ready_first');
  // the server renders without the buyer or their flags, so the line waits for both in the browser,
  // hidden in its place, instead of hydrating into a different provider or arm
  const isBuyerKnown = !isUserLoading && !!gb?.ready && gb.getAttributes().userId === user?.id;
  const [isSettled, setIsSettled] = useState(false);

  useEffect(() => {
    if (isBuyerKnown) {
      setIsSettled(true);

      return;
    }

    const fallbackTimer = setTimeout(() => setIsSettled(true), FEATURES_READY_TIMEOUT_MS);

    return () => clearTimeout(fallbackTimer);
  }, [isBuyerKnown]);

  // Don't show shipping information for sold products
  if (status === 'sold') {
    return null;
  }

  const lineProps = {
    container: true,
    className: 'info-element shipping-info-element',
    style: isSettled ? undefined : { visibility: 'hidden' as const },
  };
  const methodName = (name: string) => t('common.shipping-method.' + name);
  const checkoutOrder = orderLikeCheckout(
    shippingOptionsWithTotals,
    isSettled ? user?.shippingPrefillInfo : undefined,
    isSettled && isRankingFlagOn,
  );
  const priceOrder = orderByPrice(shippingOptionsWithTotals);
  const bundling = shippingOptionsWithTotals?.bundling;
  const isBundlingOpen = bundling?.timeLeft > 0;
  // a campaign item names its cheapest or free provider in both arms of the campaign test, so checkout
  // order only breaks ties there, and the order test leaves it out like a bundling hour
  const hasCampaign = !!getCampaignDeliveryLine(priceOrder);
  // read only where the two orders name a different provider, once GrowthBook has this buyer's attributes
  const isCheckoutOrderOn =
    isSettled &&
    isBuyerKnown &&
    !isBundlingOpen &&
    !hasCampaign &&
    checkoutOrder[0]?.name !== priceOrder[0]?.name &&
    !!gb?.isOn('pdp_delivery_line_checkout_order');
  const orderedOptions = hasCampaign || isCheckoutOrderOn ? checkoutOrder : priceOrder;
  const campaignLine = getCampaignDeliveryLine(orderedOptions);
  // read only where the line differs between arms, so GrowthBook logs exposure for those views alone,
  // and only once it has this buyer's attributes: after the fallback timeout the line shows control
  const isTestLine = isSettled && isBuyerKnown && (isBundlingOpen || !!campaignLine) && !!gb?.isOn('pdp_delivery_campaign_line');

  // If user has bundling for this product, show this instead
  if (isBundlingOpen) {
    const bundledWithMethod = bundling.bundledWithShippingMethod;
    // keyed so the countdown keeps running when the line switches arm once the buyer is known
    const timer = (
      <span key="bundling-timer">
        <span
          dangerouslySetInnerHTML={{
            __html: t('components.page-product.product-details.shipping.timer-prefix'),
          }}
        />
        <BundlingTimer timeLeft={bundling?.timeLeft} onTimerEnd={handleBundlingTimeEnd} />
        <span
          dangerouslySetInnerHTML={{
            __html: t('components.page-product.product-details.shipping.timer-suffix'),
          }}
        />
      </span>
    );

    // example: [Free delivery] with Paxi (49m 25s remaining)
    if (isTestLine && bundledWithMethod) {
      return (
        <Grid {...lineProps}>
          <span className="delivery-line-free-label">
            <strong>{t('pages.buy.free-delivery')}</strong>
          </span>
          {t('components.page-product.product-details.shipping.free-with', { shippingMethod: methodName(bundledWithMethod) })}
          &nbsp;({timer})
        </Grid>
      );
    }

    return (
      <Grid {...lineProps}>
        +{' '}
        {t('components.page-product.product-details.shipping.bundling-available', {
          shippingMethod: bundledWithMethod?.charAt(0).toUpperCase() + bundledWithMethod?.substring(1),
        })}
        &nbsp;({timer})
      </Grid>
    );
  }

  if (campaignLine && campaignLine.type !== 'regular' && isTestLine) {
    const { option } = campaignLine;

    if (campaignLine.type === 'free') {
      const suffix = {
        some: t('components.page-product.product-details.shipping.free-with', { shippingMethod: methodName(option.name) }),
        'first-order': t('components.page-product.product-details.shipping.free-on-first-order'),
        everyone: '',
      }[campaignLine.reach];

      return (
        <Grid {...lineProps}>
          <span className="delivery-line-free-label">
            <strong>{t('pages.buy.free-delivery')}</strong>
          </span>
          {suffix}
        </Grid>
      );
    }

    return (
      <Grid {...lineProps}>
        <span>
          +{' '}
          <Trans
            i18nKey="components.page-product.product-details.shipping.shipping-from-discounted"
            values={{
              originalPrice: formatPrice(option.pricing.preCampaignShipping ?? option.pricing.shipping),
              finalPrice: formatPrice(option.pricing.shipping),
              shippingMethod: methodName(option.name),
            }}
            components={{
              original: <span className="delivery-line-original-price" />,
              final: <strong className="delivery-line-final-price" />,
            }}
          />
        </span>
      </Grid>
    );
  }

  // control on a campaign item names the same provider at the same price as the test arm, only plainer
  const firstOption = campaignLine ? campaignLine.option : orderedOptions[0];

  if (!firstOption) {
    return <Grid {...lineProps}>{t('components.page-product.product-details.shipping.delivery-not-supported')}</Grid>;
  }

  return (
    <Grid {...lineProps}>
      + {/* a campaign's free price stays a price here, the free wording is the campaign test's to show */}
      {parseFloat(firstOption.pricing.shipping) === 0 && !firstOption.pricing.preCampaignShipping
        ? t('components.page-product.product-details.shipping.shipping-zero-price')
        : t('components.page-product.product-details.shipping.shipping-from', {
            shippingPrice: formatPrice(firstOption.pricing.shipping),
            shippingMethod: methodName(firstOption.name),
          })}
    </Grid>
  );
};

export default ShippingInformation;
